import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import ForgotPasswordEmailForm from "@/components/auth/ForgotPasswordEmailForm";
import ForgotPasswordPhoneForm from "@/components/auth/ForgotPasswordPhoneForm";
import PasswordEmailSent from "@/components/auth/PasswordEmailSent";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import { useLanguage } from "@/hooks/useLanguage";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2 } from "lucide-react";

/**
 * 忘记密码统一入口页面
 * 与 Login/Register 页面保持一致的布局风格
 *
 * 根据运营区域自动选择合适的密码重置方式：
 * - 国内模式 (domestic): 手机号 + 短信验证码重置密码
 * - 海外模式 (international): 邮箱链接重置密码
 *
 * Requirements: 1.1, 2.1, 3.1, 3.2, 3.3, 3.4
 */
export default function ForgotPassword() {
  const { t } = useTranslation("login");
  const [searchParams] = useSearchParams();
  const emailSent = searchParams.get("sent") === "true";
  const { isDomestic, loading, error } = useOperationRegion();
  const { changeLanguage } = useLanguage();

  // 国内模式强制设置语言为中文
  useEffect(() => {
    if (isDomestic) {
      changeLanguage("zh");
    }
  }, [isDomestic, changeLanguage]);

  // 如果邮件已发送，显示成功页面
  if (emailSent) {
    return <PasswordEmailSent />;
  }

  // 渲染表单内容
  const renderFormContent = () => {
    // 加载状态：显示骨架屏 (Requirement 3.2)
    if (loading) {
      return <ForgotPasswordSkeleton />;
    }

    // 错误状态或海外模式：显示邮箱表单 (Requirement 3.4, 2.1)
    if (error || !isDomestic) {
      return <ForgotPasswordEmailForm />;
    }

    // 国内模式：显示手机号表单 (Requirement 1.1)
    return <ForgotPasswordPhoneForm />;
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 sm:py-12">
      <div
        className="w-[300px] flex flex-col -mt-16 sm:mt-0"
        data-testid="forgot-password-container"
      >
        {loading ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* 标题区域 */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">
                {t("forgotPasswordPhone.title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isDomestic
                  ? t("forgotPasswordPhone.subtitle")
                  : t(
                      "forgotPassword.subtitle",
                      "输入您的邮箱，我们将发送重置链接",
                    )}
              </p>
            </div>

            {/* 表单内容 */}
            {renderFormContent()}

            {/* 返回登录链接 */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              {t("forgotPasswordPhone.rememberPassword", "想起密码了？")}{" "}
              <Link
                to="/login"
                className="text-foreground font-medium hover:text-primary transition-colors"
              >
                {t("forgotPasswordPhone.backToLogin")}
              </Link>
            </p>
          </>
        )}
      </div>

      {/* 底部条款 */}
      <footer className="fixed bottom-0 left-0 right-0 py-6 px-4 text-center text-xs text-muted-foreground">
        <p>
          {t("app.loginAgreement", "继续即表示您同意我们的")}{" "}
          <Link to="/terms" className="hover:text-foreground transition-colors">
            {t("app.terms", "服务条款")}
          </Link>
          {t("app.agreementMiddle", " 和 ")}{" "}
          <Link
            to="/privacy"
            className="hover:text-foreground transition-colors"
          >
            {t("app.privacy", "隐私政策")}
          </Link>
        </p>
      </footer>
    </div>
  );
}

/**
 * 忘记密码页面骨架屏
 * 在加载运营区域配置时显示
 */
function ForgotPasswordSkeleton() {
  return (
    <div className="space-y-4" data-testid="forgot-password-skeleton">
      {/* 输入框占位 */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <div className="flex gap-2">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>

      {/* 验证码输入框占位 */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-10 w-full" />
      </div>

      {/* 密码输入框占位 */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-10 w-full" />
      </div>

      {/* 确认密码输入框占位 */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-10 w-full" />
      </div>

      {/* 按钮占位 */}
      <Skeleton className="h-10 w-full" />
    </div>
  );
}
